import { useState, useRef, type FormEvent } from 'react';

const serviceOptions = [
  'IT Hardware',
  'Networking',
  'Laptop & Desktop',
  'Printers & Xerox Machines',
  'AMC & IT Support',
  'Other',
];

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('Thank you! We\'ll get back to you shortly.');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    setIsSubmitting(true);
    setFormStatus('idle');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      if (honeypotEl) formData.delete('phone_alt');

      // const response = await fetch('https://readdy.ai/api/form/d9fko5elb8e5uktkmu5g', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      //   body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      // });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you! We\'ll get back to you shortly.');
        form.reset();
      } else if (isSpam) {
        setFormStatus('error');
        setFormMessage('Submission could not be processed. Please try again.');
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
      if (formStatus === 'success') {
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-background-50 relative">
      <div className="max-w-site mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="flex-1 animate-on-scroll">
            <span className="font-label text-xs uppercase tracking-[0.2em] text-foreground-500">Contact</span>
            <h2 className="mt-4 font-heading text-[36px] md:text-[48px] font-bold text-foreground-950 tracking-[-0.02em] leading-[1.14]">
              Let&apos;s talk.
            </h2>
            <p className="mt-4 text-base text-foreground-600 leading-relaxed max-w-[460px]">
              Have an IT requirement, service request or need ongoing technical support? Get in touch with TechBridge.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary-50 group-hover:bg-primary-500 transition-colors duration-300">
                  <i className="ri-phone-line text-primary-500 group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <p className="font-label text-[11px] uppercase tracking-[0.12em] text-foreground-400 mb-1">Call</p>
                  <a href="tel:+919940148039" className="text-base font-semibold text-foreground-950 hover:text-primary-500 transition-colors">+91 99401 48039</a>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary-50 group-hover:bg-primary-500 transition-colors duration-300">
                  <i className="ri-mail-line text-primary-500 group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <p className="font-label text-[11px] uppercase tracking-[0.12em] text-foreground-400 mb-1">Email</p>
                  <a href="mailto:techbridge32@gmail.com" className="text-base font-semibold text-foreground-950 hover:text-primary-500 transition-colors">techbridge32@gmail.com</a>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary-50 group-hover:bg-primary-500 transition-colors duration-300">
                  <i className="ri-map-pin-line text-primary-500 group-hover:text-white transition-colors duration-300"></i>
                </div>
                <div>
                  <p className="font-label text-[11px] uppercase tracking-[0.12em] text-foreground-400 mb-1">Visit</p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    TechBridge Systems &amp; Solutions<br />
                    No. 5/2, Loyal Housing Green Garden,<br />
                    Thiruvalluvar Street, Omsakthi Nagar,<br />
                    Kallikuppam, Ambattur,<br />
                    Chennai – 600058
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 animate-on-scroll">
            <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="bg-white border border-background-200 rounded-2xl p-6 md:p-8 shadow-sm shadow-background-200/50 hover:shadow-md hover:shadow-background-200/60 transition-shadow duration-300">
              <div className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block font-label text-xs font-semibold text-foreground-700 mb-2">Your Name *</label>
                  <div className="relative">
                    <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full rounded-xl border border-background-200 bg-background-50 pl-10 pr-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none transition-all duration-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10"
                      placeholder="Enter your name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-phone" className="block font-label text-xs font-semibold text-foreground-700 mb-2">Phone Number *</label>
                  <div className="relative">
                    <i className="ri-phone-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      required
                      className="w-full rounded-xl border border-background-200 bg-background-50 pl-10 pr-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none transition-all duration-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-service" className="block font-label text-xs font-semibold text-foreground-700 mb-2">Service Required *</label>
                  <div className="relative">
                    <i className="ri-list-check absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                    <select
                      id="contact-service"
                      name="service"
                      required
                      defaultValue=""
                      className="w-full rounded-xl border border-background-200 bg-background-50 pl-10 pr-4 py-3 text-sm text-foreground-950 outline-none transition-all duration-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 appearance-none cursor-pointer"
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235F6C85' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', paddingRight: '40px' }}
                    >
                      <option value="" disabled>Select a service</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block font-label text-xs font-semibold text-foreground-700 mb-2">Message</label>
                  <div className="relative">
                    <i className="ri-chat-3-line absolute left-4 top-3.5 text-foreground-400 text-sm"></i>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full rounded-xl border border-background-200 bg-background-50 pl-10 pr-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none transition-all duration-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 resize-none"
                      placeholder="Tell us briefly about your requirement..."
                    ></textarea>
                  </div>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-xl font-label text-sm font-semibold transition-all duration-300 cursor-pointer group disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-xl hover:shadow-primary-500/20 hover:-translate-y-0.5"
                  style={{ backgroundColor: '#0167F8', color: '#FFFFFF', padding: '14px 28px' }}
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Enquiry
                      <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
                    </>
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800 font-medium text-center flex items-center justify-center gap-2">
                    <i className="ri-check-line"></i>
                    {formMessage}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800 font-medium text-center flex items-center justify-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {formMessage}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}